// Canvas overlays drawn in image space: tape lines, perspective-correct target
// regions, detected feet and an optional skeleton. Used on the live view, the
// replay and the evidence stills.

import { ellipsePoints, type Vec2 } from '../../core/geometry';
import { toImage, toPlane, tapeOverlay, type PlaneMapping, type Stance } from '../../core/mapping';
import { LM, POSE_EDGES, lmVis, lmX, lmY } from '../../core/pose';
import type { Reference } from '../../core/reference';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Where an image of size srcW×srcH lands inside an element using object-fit: contain. */
export function containRect(elW: number, elH: number, srcW: number, srcH: number): Rect {
  if (!srcW || !srcH) return { x: 0, y: 0, w: elW, h: elH };
  const s = Math.min(elW / srcW, elH / srcH);
  const w = srcW * s;
  const h = srcH * s;
  return { x: (elW - w) / 2, y: (elH - h) / 2, w, h };
}

export const COLORS = {
  front: '#3987e5',
  rear: '#d55181',
  tape: '#5eead4',
  halo: 'rgba(5, 10, 14, 0.75)',
  skeleton: 'rgba(255, 255, 255, 0.55)',
  text: '#eef3f6',
};

export interface OverlayInput {
  mapping?: PlaneMapping | null;
  tape?: { stripLength: number; gap: number } | null;
  reference?: Reference | null;
  tol?: { along: number; across: number } | null;
  stance?: Stance;
  lm?: Float32Array | null;
  skeleton?: boolean;
  /** Dim the target regions (e.g. while the rider is lying down). */
  quietTargets?: boolean;
}

export function drawOverlay(ctx: CanvasRenderingContext2D, r: Rect, o: OverlayInput, dpr = 1): void {
  const P = (p: Vec2) => ({ x: r.x + p.x * r.w, y: r.y + p.y * r.h });
  const lw = (px: number) => px * dpr;

  if (o.mapping && o.tape) {
    const t = tapeOverlay(o.mapping, o.tape);
    ctx.save();
    ctx.globalAlpha = 0.55;
    for (const [a, b] of [t.stringer, t.rear, t.front]) line(ctx, P(a), P(b), COLORS.tape, lw(2));
    ctx.restore();
  }

  if (o.mapping && o.reference && o.tol) {
    ctx.save();
    ctx.globalAlpha = o.quietTargets ? 0.45 : 1;
    // Labels only when the targets are far enough apart on screen to read.
    const fp = P(toImage(o.mapping, o.reference.front.centre));
    const rp = P(toImage(o.mapping, o.reference.rear.centre));
    const showLabels = Math.hypot(fp.x - rp.x, fp.y - rp.y) > lw(70);
    for (const role of ['front', 'rear'] as const) {
      const c = o.reference[role].centre;
      const poly = ellipsePoints(c, o.tol.across, o.tol.along).map((p) => P(toImage(o.mapping!, p)));
      ctx.beginPath();
      poly.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.closePath();
      ctx.fillStyle = role === 'front' ? 'rgba(57,135,229,0.16)' : 'rgba(213,81,129,0.16)';
      ctx.fill();
      ctx.lineWidth = lw(3.5);
      ctx.strokeStyle = COLORS.halo;
      ctx.stroke();
      ctx.lineWidth = lw(1.75);
      ctx.strokeStyle = COLORS[role];
      ctx.stroke();
      if (showLabels) {
        const lp = role === 'front' ? fp : rp;
        label(ctx, role === 'front' ? 'front' : 'back', lp.x, lp.y - lw(18), lw(12));
      }
    }
    ctx.restore();
  }

  const lm = o.lm;
  if (!lm) return;
  if (o.skeleton) {
    ctx.save();
    ctx.lineCap = 'round';
    for (const [a, b] of POSE_EDGES) {
      if (lmVis(lm, a) < 0.4 || lmVis(lm, b) < 0.4) continue;
      line(ctx, P({ x: lmX(lm, a), y: lmY(lm, a) }), P({ x: lmX(lm, b), y: lmY(lm, b) }), COLORS.skeleton, lw(2));
    }
    ctx.restore();
  }
  for (const f of feetFromLandmarks(lm, o.mapping ?? null, o.stance ?? 'regular')) {
    const h = P(f.heel);
    const t = P(f.toe);
    const color = COLORS[f.role];
    ctx.globalAlpha = f.visible ? 1 : 0.35;
    line(ctx, h, t, COLORS.halo, lw(8));
    line(ctx, h, t, color, lw(4));
    dot(ctx, h, lw(5), color);
    dot(ctx, t, lw(5), color);
    ctx.globalAlpha = 1;
  }
}

export interface DetectedFoot {
  role: 'front' | 'rear';
  heel: Vec2;
  toe: Vec2;
  visible: boolean;
}

/** Feet from raw landmarks; front = nearer the nose when the floor mapping is known. */
export function feetFromLandmarks(lm: Float32Array, mapping: PlaneMapping | null, stance: Stance): DetectedFoot[] {
  const foot = (heel: number, toe: number) => ({
    heel: { x: lmX(lm, heel), y: lmY(lm, heel) },
    toe: { x: lmX(lm, toe), y: lmY(lm, toe) },
    visible: Math.min(lmVis(lm, heel), lmVis(lm, toe)) >= 0.5,
  });
  const L = foot(LM.leftHeel, LM.leftToe);
  const R = foot(LM.rightHeel, LM.rightToe);
  let leftIsFront = stance === 'regular';
  if (mapping) {
    const y = (f: typeof L) => (toPlane(mapping, f.heel).y + toPlane(mapping, f.toe).y) / 2;
    const yl = y(L);
    const yr = y(R);
    if (Number.isFinite(yl) && Number.isFinite(yr)) leftIsFront = yl >= yr;
  }
  return [
    { role: leftIsFront ? 'front' : 'rear', ...L },
    { role: leftIsFront ? 'rear' : 'front', ...R },
  ];
}

function line(ctx: CanvasRenderingContext2D, a: Vec2, b: Vec2, color: string, width: number) {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
}

function dot(ctx: CanvasRenderingContext2D, p: Vec2, r: number, color: string) {
  ctx.beginPath();
  ctx.arc(p.x, p.y, r + r * 0.5, 0, Math.PI * 2);
  ctx.fillStyle = COLORS.halo;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
}

function label(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, size: number) {
  ctx.font = `600 ${size}px -apple-system, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = size / 3;
  ctx.strokeStyle = COLORS.halo;
  ctx.strokeText(text, x, y);
  ctx.fillStyle = COLORS.text;
  ctx.fillText(text, x, y);
}

/** Draws a numbered marker (tape-marking step). */
export function drawMarker(ctx: CanvasRenderingContext2D, p: Vec2, text: string, color: string, dpr = 1) {
  ctx.beginPath();
  ctx.arc(p.x, p.y, 13 * dpr, 0, Math.PI * 2);
  ctx.fillStyle = COLORS.halo;
  ctx.fill();
  ctx.lineWidth = 2.5 * dpr;
  ctx.strokeStyle = color;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(p.x, p.y, 2.5 * dpr, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  label(ctx, text, p.x, p.y - 24 * dpr, 13 * dpr);
}
